import { useQuery } from '@tanstack/react-query';
import type { Leco, LecoType } from '@/api/learning.types';
import { mobietrainGraphql } from './graphql';

const GET_FEATURED_PATH_QUERY = `
  query GetFeaturedPath {
    users {
      getFeaturedPath {
        id
        slug
        avatar
        progress
        type
        origin
        unlocked
        completed
        categories
      }
    }
  }
`;

interface RawFeaturedPath {
  id: string;
  slug: string;
  avatar: string | null;
  progress: number | null;
  type: string | null;
  origin: string | null;
  unlocked: boolean | null;
  completed: boolean | null;
  categories: string[] | null;
}

interface GetFeaturedPathResponse {
  users: {
    getFeaturedPath: RawFeaturedPath | null;
  };
}

const toLecoType = (raw: string | null): LecoType => {
  switch (raw) {
    case 'crash_course':
    case 'crash-course':
      return 'crash_course';
    case 'event':
      return 'event';
    case 'scorm':
      return 'scorm';
    case 'video':
      return 'video';
    case 'text':
      return 'text';
    case 'questions_only':
      return 'questions_only';
    case 'single_learning':
      return 'single_learning';
    default:
      return 'legacy';
  }
};

const adaptFeaturedPath = (raw: RawFeaturedPath): Leco => ({
  id: raw.id,
  title: raw.slug,
  image: raw.avatar,
  progress: raw.progress ?? 0,
  unlocked: Boolean(raw.unlocked),
  type: toLecoType(raw.type),
  content: {
    id: raw.id,
    lecoId: Number(raw.id),
    completedAt: null,
    lockedBy: [],
  },
});

interface UseFeaturedPathOptions {
  enabled?: boolean;
}

export const useFeaturedPath = ({ enabled = true }: UseFeaturedPathOptions = {}) => {
  return useQuery({
    queryKey: ['featured-path'],
    queryFn: async (): Promise<Leco | null> => {
      const res = await mobietrainGraphql<GetFeaturedPathResponse>(GET_FEATURED_PATH_QUERY);
      const raw = res.users.getFeaturedPath;
      return raw ? adaptFeaturedPath(raw) : null;
    },
    enabled,
    retry: 1,
  });
};
